/**
 * Level 0 — the Lobby.
 *
 * One enormous floor plate that has been subdivided badly, rather than a
 * building made of rooms. The whole interior is carved open first; everything
 * after that is *partial* division of a space that was already continuous.
 *
 * Three things do the work, in this order:
 *
 *  - **Structural dividers.** A recursive split lays full-height walls across
 *    the slab, but a divider is a wall with two to four openings six to twelve
 *    metres wide, or a wall that only covers part of its line and stops, or —
 *    a third of the time — no wall at all. So a plate is a district rather than
 *    a room, and you can usually see into the next one.
 *  - **Freestanding runs.** Inside a plate: walls that begin and end in mid-air,
 *    L-corners, and U-shaped recesses that make alcoves instead of rooms. Each
 *    is placed as an island in open floor, which is what makes it read as
 *    subdivision rather than as architecture — and, conveniently, is also what
 *    makes it impossible for one to cut the level in two.
 *  - **Columns.** One square lattice at a single pitch across the entire level,
 *    laid last. The colonnade in one plate lines up with the colonnade in the
 *    next, and the eye reads the whole floor as one structure.
 *
 * Enclosed rooms still exist. They are the exception: a small box standing in
 * the middle of an acre of carpet with one door in it. So are corridors — one
 * or two per level, built rather than carved, and worth something to find,
 * because a corridor is the only thing here that tells you which way you came.
 */

import { Cell, type Grid, type Region, type RNG } from '../../core/types';
import { scatterDamp, scatterOpenCeilings } from './decorate';
import {
  addRegion,
  carveLine,
  fillRect,
  guardedEdit,
  inset,
  interiorRect,
  makeGuard,
  strokeRect,
  type GenSite,
  type Rect,
} from './site';
import { applyWrongness } from './wrongness';

/** Smallest plate dimension, in cells. At 3 m a cell this is a 36 m span. */
const MIN_PLATE = 13;
/** Area the split drifts toward before it starts accepting plates. */
const TARGET_PLATE = 430;
/** Plates above this always split again, so no single district eats the map. */
const MAX_PLATE_AREA = 1250;
/** Smallest plate that can qualify as an `atrium`, and the solid share it may hold. */
const ATRIUM_AREA = 380;
const ATRIUM_SOLID = 0.1;

/** Chance a divider line is simply not built, merging two plates into one space. */
const DIVIDER_NONE = 0.32;
/** Chance a divider covers only part of its line and stops in mid-air. */
const DIVIDER_PARTIAL = 0.24;
/** Openings punched in a full divider, and their width in cells. */
const DIVIDER_GAPS: readonly [number, number] = [2, 4];
const GAP_WIDTH: readonly [number, number] = [2, 4];

/** Cells of plate per freestanding structure placed inside it. */
const AREA_PER_STRUCTURE = 105;
/** Attempts to find an island of clear floor for one structure. */
const SPOT_TRIES = 20;
/**
 * Clear floor a structure needs on every side of it, in cells. Two rather than
 * one: at one cell two structures can land a single cell apart, and the slot
 * between them reads as a corridor somebody forgot to finish. Six metres of
 * floor between one wall and the next is what keeps them separate objects
 * standing in a space rather than a partition system.
 */
const STRUCTURE_CLEARANCE = 2;
/** Cells of plate a structure keeps clear of the plate's own boundary. */
const PLATE_MARGIN = 2;

/** Cells of flanking wall plus gap in one corridor cycle, and the gap's share. */
const CORRIDOR_CYCLE = 16;
const CORRIDOR_BREAK = 4;

/** Column pitch in cells. One value for the whole level. */
const COLUMN_PITCH: readonly [number, number] = [3, 4];

const DX = [1, -1, 0, 0];
const DY = [0, 0, 1, -1];

/** Carve the Lobby profile into `site`. */
export function generateLobby(site: GenSite): void {
  const { grid, rng } = site;
  fillRect(grid, interiorRect(grid), Cell.Floor);

  const plates: Rect[] = [];
  splitPlates(site, interiorRect(grid), plates);
  // Registered before anything is built inside them, so the small spaces added
  // later — alcoves, boxes, the warren — win the region lookup where they sit.
  const plateRegions = plates.map((plate) => addRegion(site, plate, 'pillar'));
  for (const plate of plates) subdivide(site, plate);

  if (rng.chance(0.5)) carveWarrenBlock(site, plates);
  carveSpineCorridors(site, rng.int(1, 2));

  // Level 0 is the level the player learns the building from, so it is the one
  // that has to stop making sense. Weighted for the new massing: a blank block
  // standing in the middle of a plate, and a wall growing out of something and
  // stopping, both read from thirty metres away in a way they never did in a
  // corridor. The ring variant of the inner room is off — on a plate this size
  // it would turn a district into a doughnut of solid rock.
  applyWrongness(site, {
    innerRooms: [8, 13],
    doorsToNothing: [4, 8],
    perforatedWalls: [2, 4],
    pillarsInDoorways: [1, 3],
    wallFins: [10, 16],
    finLength: [3, 7],
    tightPillarPatches: [3, 5],
    ringRooms: false,
  });

  // Columns last. A `Cell.Pillar` is walkable, so it can never cut anything off
  // and nothing above here has to work around one; and by going last the
  // lattice can see the finished floor and decline wherever the space has
  // already become too tight to stand a column in.
  columnGrid(site, rng.int(COLUMN_PITCH[0], COLUMN_PITCH[1]));
  labelPlates(site, plateRegions);

  scatterDamp(site, rng.int(18, 30), 8, 44);
  if (rng.chance(0.55)) scatterOpenCeilings(site, 1, 2, 3);
}

/**
 * Name each plate for what it turned out to be rather than for how big it was
 * drawn. A plate that ends up carrying its column grid and little else is an
 * `atrium` — a district you can see right across; anything with real structure
 * standing in it is a `pillar` area. Placement, lighting and the map all read
 * these, so they have to describe the space that exists, not the intent.
 */
function labelPlates(site: GenSite, plates: readonly Region[]): void {
  const { grid } = site;
  for (const plate of plates) {
    const area = plate.w * plate.h;
    if (area < ATRIUM_AREA) continue;
    let solid = 0;
    for (let y = plate.y; y < plate.y + plate.h; y++) {
      for (let x = plate.x; x < plate.x + plate.w; x++) if (grid.isSolid(x, y)) solid++;
    }
    if (solid / area <= ATRIUM_SOLID) plate.kind = 'atrium';
  }
}

/* ------------------------------------------------------------------ *
 * Plates and dividers
 * ------------------------------------------------------------------ */

function splitPlates(site: GenSite, rect: Rect, out: Rect[]): void {
  const { rng } = site;
  const area = rect.w * rect.h;
  const canX = rect.w >= 2 * MIN_PLATE + 1;
  const canY = rect.h >= 2 * MIN_PLATE + 1;
  if (!canX && !canY) {
    out.push(rect);
    return;
  }
  if (area <= MAX_PLATE_AREA && area < rng.range(TARGET_PLATE * 0.55, TARGET_PLATE * 1.9)) {
    out.push(rect);
    return;
  }

  // Split the longer side unless the plate is near square, so districts stay
  // wide in both directions instead of degenerating into strips.
  let vertical: boolean;
  if (!canY) vertical = true;
  else if (!canX) vertical = false;
  else if (rect.w > rect.h * 1.2) vertical = true;
  else if (rect.h > rect.w * 1.2) vertical = false;
  else vertical = rng.chance(0.5);

  const span = vertical ? rect.w : rect.h;
  const origin = vertical ? rect.x : rect.y;
  const cut = origin + rng.int(MIN_PLATE, span - 1 - MIN_PLATE);

  buildDivider(site, rect, cut, vertical);

  if (vertical) {
    splitPlates(site, { x: rect.x, y: rect.y, w: cut - rect.x, h: rect.h }, out);
    splitPlates(site, { x: cut + 1, y: rect.y, w: rect.x + rect.w - cut - 1, h: rect.h }, out);
  } else {
    splitPlates(site, { x: rect.x, y: rect.y, w: rect.w, h: cut - rect.y }, out);
    splitPlates(site, { x: rect.x, y: cut + 1, w: rect.w, h: rect.y + rect.h - cut - 1 }, out);
  }
}

/**
 * The wall on one split line, or the absence of one.
 *
 * A divider is never allowed to close: the gapped form keeps two to four
 * openings and the partial form leaves most of the line open, so no plate can
 * ever be sealed off by its own boundary. That is a structural guarantee rather
 * than a check, which is why the split can recurse without ever flood-filling.
 */
function buildDivider(site: GenSite, rect: Rect, cut: number, vertical: boolean): void {
  const { grid, rng } = site;
  const crossOrigin = vertical ? rect.y : rect.x;
  const crossSpan = vertical ? rect.h : rect.w;
  const put = (t: number, cell: Cell): void => {
    if (vertical) grid.set(cut, t, cell);
    else grid.set(t, cut, cell);
  };

  const roll = rng.next();
  if (roll < DIVIDER_NONE) return;

  if (roll < DIVIDER_NONE + DIVIDER_PARTIAL) {
    // A wall that covers part of its line and stops. Anchored at one end most
    // of the time, because a divider that grows out of the building and gives
    // up halfway is stranger than one just floating in the middle of the floor.
    const len = Math.round(crossSpan * rng.range(0.3, 0.7));
    const anchored = rng.chance(0.7);
    const start = anchored
      ? rng.chance(0.5)
        ? crossOrigin
        : crossOrigin + crossSpan - len
      : crossOrigin + rng.int(1, Math.max(1, crossSpan - len - 1));
    for (let k = 0; k < len; k++) put(start + k, Cell.Wall);
    return;
  }

  for (let t = crossOrigin; t < crossOrigin + crossSpan; t++) put(t, Cell.Wall);

  // Wide openings, not doorways: you should be able to see the next district
  // through them from across this one.
  const gaps = rng.int(DIVIDER_GAPS[0], DIVIDER_GAPS[1]);
  for (let g = 0; g < gaps; g++) {
    const band = crossSpan / gaps;
    const centre = crossOrigin + Math.round(band * (g + 0.5) + rng.range(-band * 0.28, band * 0.28));
    const width = rng.int(GAP_WIDTH[0], GAP_WIDTH[1]);
    for (let k = 0; k < width; k++) {
      const t = centre - (width >> 1) + k;
      if (t < crossOrigin || t >= crossOrigin + crossSpan) continue;
      put(t, Cell.Floor);
    }
  }
}

/* ------------------------------------------------------------------ *
 * Partial subdivision inside a plate
 * ------------------------------------------------------------------ */

/**
 * Fill a plate with structures that divide it without enclosing it.
 *
 * Every one of them is placed as an island: the whole footprint and its
 * clearance margin must be plain floor. That is the rule that makes these read
 * as partial subdivision — a wall that meets another wall has made a room — and
 * it is also why none of this needs a connectivity guard. None of these shapes
 * closes on itself, so whatever a U encloses is still reachable by walking
 * around the outside of it, and a box always has its door punched.
 */
function subdivide(site: GenSite, plate: Rect): void {
  const { rng } = site;
  const budget = Math.max(2, Math.round((plate.w * plate.h) / AREA_PER_STRUCTURE));
  for (let i = 0; i < budget; i++) {
    const roll = rng.next();
    if (roll < 0.42) placeRun(site, plate);
    else if (roll < 0.68) placeEll(site, plate);
    else if (roll < 0.9) placeRecess(site, plate);
    else placeBox(site, plate);
  }
}

/** A straight wall that starts in open floor and stops in open floor. */
function placeRun(site: GenSite, plate: Rect): void {
  const { grid, rng } = site;
  const horizontal = rng.chance(0.5);
  const room = (horizontal ? plate.w : plate.h) - 4;
  const len = span(rng, 5, 16, room);
  if (len <= 0) return;
  const spot = findSpot(site, plate, horizontal ? len : 1, horizontal ? 1 : len);
  if (spot) fillRect(grid, spot, Cell.Wall);
}

/** Two runs meeting at a corner. Shelters one quadrant and nothing else. */
function placeEll(site: GenSite, plate: Rect): void {
  const { grid, rng } = site;
  const a = span(rng, 3, 9, plate.w - 4);
  const b = span(rng, 3, 8, plate.h - 4);
  if (a <= 0 || b <= 0) return;
  const spot = findSpot(site, plate, a, b);
  if (!spot) return;
  const cx = rng.chance(0.5) ? spot.x : spot.x + a - 1;
  const cy = rng.chance(0.5) ? spot.y : spot.y + b - 1;
  carveLine(grid, spot.x, cy, spot.x + a - 1, cy, Cell.Wall);
  carveLine(grid, cx, spot.y, cx, spot.y + b - 1, Cell.Wall);
}

/**
 * A U: a back wall with a short return at each end, open along its whole fourth
 * side. The mouth is at least three cells — nine metres — wide, which is what
 * keeps it an alcove rather than a room with the door taken off.
 *
 * Occasionally built twice, side by side, identical. Two recesses the same size
 * and shape in the same wall line is the cheapest way to make somebody certain
 * they have already been here.
 */
function placeRecess(site: GenSite, plate: Rect): void {
  const { rng } = site;
  const alongX = rng.chance(0.5);
  const len = span(rng, 5, 10, (alongX ? plate.w : plate.h) - 4);
  const depth = span(rng, 2, 4, (alongX ? plate.h : plate.w) - 4);
  if (len <= 0 || depth <= 0) return;
  const w = alongX ? len : depth;
  const h = alongX ? depth : len;
  const spot = findSpot(site, plate, w, h);
  if (!spot) return;
  const backFirst = rng.chance(0.5);
  buildRecess(site, spot, alongX, backFirst);

  if (!rng.chance(0.16)) return;
  const gap = rng.int(2, 4);
  const twin: Rect = alongX
    ? { x: spot.x + w + gap, y: spot.y, w, h }
    : { x: spot.x, y: spot.y + h + gap, w, h };
  if (fits(plate, twin) && islandClear(site.grid, twin)) buildRecess(site, twin, alongX, backFirst);
}

function buildRecess(site: GenSite, r: Rect, alongX: boolean, backFirst: boolean): void {
  const { grid } = site;
  if (alongX) {
    const back = backFirst ? r.y : r.y + r.h - 1;
    const dir = backFirst ? 1 : -1;
    carveLine(grid, r.x, back, r.x + r.w - 1, back, Cell.Wall);
    for (let k = 1; k < r.h; k++) {
      grid.set(r.x, back + dir * k, Cell.Wall);
      grid.set(r.x + r.w - 1, back + dir * k, Cell.Wall);
    }
    addRegion(site, { x: r.x + 1, y: backFirst ? r.y + 1 : r.y, w: r.w - 2, h: r.h - 1 }, 'closet');
  } else {
    const back = backFirst ? r.x : r.x + r.w - 1;
    const dir = backFirst ? 1 : -1;
    carveLine(grid, back, r.y, back, r.y + r.h - 1, Cell.Wall);
    for (let k = 1; k < r.w; k++) {
      grid.set(back + dir * k, r.y, Cell.Wall);
      grid.set(back + dir * k, r.y + r.h - 1, Cell.Wall);
    }
    addRegion(site, { x: backFirst ? r.x + 1 : r.x, y: r.y + 1, w: r.w - 1, h: r.h - 2 }, 'closet');
  }
}

/**
 * An enclosed room: a box with one door standing on its own in the open. These
 * are the exception on this level and should stay that way — a room you have to
 * walk into is only interesting when almost nothing else is.
 */
function placeBox(site: GenSite, plate: Rect): void {
  const { grid, rng } = site;
  const bw = span(rng, 4, 8, plate.w - 4);
  const bh = span(rng, 4, 7, plate.h - 4);
  if (bw <= 0 || bh <= 0) return;
  const spot = findSpot(site, plate, bw, bh);
  if (!spot) return;
  strokeRect(grid, spot, Cell.Wall);
  const inner = inset(spot, 1);
  fillRect(grid, inner, Cell.Floor);
  punchBoxDoor(site, spot);
  if (rng.chance(0.2)) punchBoxDoor(site, spot);
  addRegion(site, inner, inner.w * inner.h >= 9 ? 'room' : 'closet');
}

/** One doorway in the middle stretch of one wall of a box, never in a corner. */
function punchBoxDoor(site: GenSite, r: Rect): void {
  const { grid, rng } = site;
  const side = rng.int(0, 3);
  if (side === 0) grid.set(rng.int(r.x + 1, r.x + r.w - 2), r.y, Cell.Doorway);
  else if (side === 1) grid.set(rng.int(r.x + 1, r.x + r.w - 2), r.y + r.h - 1, Cell.Doorway);
  else if (side === 2) grid.set(r.x, rng.int(r.y + 1, r.y + r.h - 2), Cell.Doorway);
  else grid.set(r.x + r.w - 1, rng.int(r.y + 1, r.y + r.h - 2), Cell.Doorway);
}

/** A length in `[lo, hi]` that still fits in `room` cells, or 0 when none does. */
function span(rng: RNG, lo: number, hi: number, room: number): number {
  const top = Math.min(hi, room);
  return top < lo ? 0 : rng.int(lo, top);
}

/** True when `r` sits inside the plate with the margin `findSpot` leaves. */
function fits(plate: Rect, r: Rect): boolean {
  return (
    r.x >= plate.x + PLATE_MARGIN &&
    r.y >= plate.y + PLATE_MARGIN &&
    r.x + r.w <= plate.x + plate.w - PLATE_MARGIN &&
    r.y + r.h <= plate.y + plate.h - PLATE_MARGIN
  );
}

/** Somewhere in the plate a `w` by `h` structure can stand as an island. */
function findSpot(site: GenSite, plate: Rect, w: number, h: number): Rect | null {
  const { grid, rng } = site;
  const x0 = plate.x + PLATE_MARGIN;
  const y0 = plate.y + PLATE_MARGIN;
  const x1 = plate.x + plate.w - PLATE_MARGIN - w;
  const y1 = plate.y + plate.h - PLATE_MARGIN - h;
  if (x1 < x0 || y1 < y0) return null;
  for (let tries = 0; tries < SPOT_TRIES; tries++) {
    const r: Rect = { x: rng.int(x0, x1), y: rng.int(y0, y1), w, h };
    if (islandClear(grid, r)) return r;
  }
  return null;
}

/** True when `r`, grown by the clearance margin, is all plain undecorated floor. */
function islandClear(grid: Grid, r: Rect): boolean {
  const pad = STRUCTURE_CLEARANCE;
  for (let y = r.y - pad; y < r.y + r.h + pad; y++) {
    for (let x = r.x - pad; x < r.x + r.w + pad; x++) {
      if (grid.at(x, y) !== Cell.Floor) return false;
    }
  }
  return true;
}

/* ------------------------------------------------------------------ *
 * The exceptions: a corridor, and a knot of small rooms
 * ------------------------------------------------------------------ */

/**
 * A corridor. On an open floor plate one has to be built rather than carved:
 * two parallel walls with a lane between them, run most of the way across the
 * map. Each flank is broken every sixteen cells and the two sides are half a
 * cycle out of step, so a corridor is a thing you step into and out of rather
 * than a tube.
 *
 * It is the one structure here that spans plates instead of sitting inside one,
 * so it is also the only one that could seal a strip of the level off — most
 * often by laying a flank across the mouth of an alcove. The guard catches
 * that, and a rejected corridor is retried somewhere else rather than dropped,
 * because a level with no corridor at all has lost its only landmark.
 */
function carveSpineCorridors(site: GenSite, count: number): void {
  const { grid, rng } = site;
  const guard = makeGuard(grid);
  for (let i = 0; i < count; i++) {
    for (let attempt = 0; attempt < 4; attempt++) {
      const horizontal = rng.chance(0.5);
      const along = horizontal ? grid.w : grid.h;
      const cross = horizontal ? grid.h : grid.w;
      const width = rng.chance(0.25) ? 2 : 1;
      const lane = rng.int(6, cross - 7 - width);
      const len = Math.floor(along * rng.range(0.55, 0.9));
      const start = rng.int(2, Math.max(2, along - len - 2));
      const end = Math.min(start + len, along - 1);
      const phase = rng.int(0, CORRIDOR_CYCLE - 1);

      const built = guardedEdit(guard, () => {
        for (let t = start; t < end; t++) {
          for (let k = 0; k < width; k++) {
            if (horizontal) grid.set(t, lane + k, Cell.Floor);
            else grid.set(lane + k, t, Cell.Floor);
          }
          for (let s = 0; s < 2; s++) {
            const at = (t + phase + s * (CORRIDOR_CYCLE >> 1)) % CORRIDOR_CYCLE;
            if (at >= CORRIDOR_CYCLE - CORRIDOR_BREAK) continue;
            const flank = s === 0 ? lane - 1 : lane + width;
            if (horizontal) grid.set(t, flank, Cell.Wall);
            else grid.set(flank, t, Cell.Wall);
          }
        }
      });
      if (!built) continue;

      // Labelled only once it stands. A `hall` over open floor would tell
      // lighting, placement and the map about a corridor that is not there.
      addRegion(
        site,
        horizontal
          ? { x: start, y: lane, w: end - start, h: width }
          : { x: lane, y: start, w: width, h: end - start },
        'hall',
      );
      break;
    }
  }
}

/**
 * A knot of tiny rooms, walled off from the floor around it and reached through
 * one or two doors: a spine corridor with closets off it, of the sort this
 * level used to be made of entirely. Sitting in the middle of an open plate it
 * reads as a piece of a different building that was dropped in here, which is
 * the only reason it is still worth having.
 */
function carveWarrenBlock(site: GenSite, plates: readonly Rect[]): void {
  const { grid, rng } = site;
  const hosts = plates.filter((p) => p.w >= 17 && p.h >= 17);
  if (hosts.length === 0) return;
  const plate = rng.pick(hosts);
  const w = span(rng, 9, 13, plate.w - 4);
  const h = span(rng, 9, 13, plate.h - 4);
  if (w <= 0 || h <= 0) return;
  const spot = findSpot(site, plate, w, h);
  if (!spot) return;

  addRegion(site, spot, 'warren');
  const guard = makeGuard(grid);
  guardedEdit(guard, () => {
    strokeRect(grid, spot, Cell.Wall);
    punchBoxDoor(site, spot);
    if (rng.chance(0.5)) punchBoxDoor(site, spot);
    carveWarren(site, inset(spot, 1));
  });
}

function carveWarren(site: GenSite, r: Rect): void {
  const { grid, rng } = site;
  fillRect(grid, r, Cell.Wall);
  const horizontal = r.w >= r.h;
  const spine = horizontal ? r.y + (r.h >> 1) : r.x + (r.w >> 1);

  if (horizontal) carveLine(grid, r.x, spine, r.x + r.w - 1, spine, Cell.Floor);
  else carveLine(grid, spine, r.y, spine, r.y + r.h - 1, Cell.Floor);

  const along = horizontal ? r.w : r.h;
  const alongStart = horizontal ? r.x : r.y;
  for (let t = alongStart + 1; t < alongStart + along - 1; t += 2) {
    for (let s = 0; s < 2; s++) {
      if (!rng.chance(0.72)) continue;
      const dir = s === 0 ? -1 : 1;
      const edge = horizontal
        ? dir < 0
          ? spine - r.y
          : r.y + r.h - 1 - spine
        : dir < 0
          ? spine - r.x
          : r.x + r.w - 1 - spine;
      if (edge <= 0) continue;
      const len = rng.int(1, edge);
      for (let k = 1; k <= len; k++) {
        if (horizontal) grid.set(t, spine + dir * k, Cell.Floor);
        else grid.set(spine + dir * k, t, Cell.Floor);
      }
      // A stub that widens at the far end is a closet rather than a nub.
      let wide = 0;
      if (len >= 2 && rng.chance(0.35)) {
        const side = rng.chance(0.5) ? 1 : -1;
        if (horizontal) grid.set(t + side, spine + dir * len, Cell.Floor);
        else grid.set(spine + dir * len, t + side, Cell.Floor);
        wide = side;
      }
      // Label the stub so supply placement knows to favour it.
      if (len >= 2) {
        const near = spine + (dir < 0 ? -len : 1);
        addRegion(
          site,
          horizontal
            ? { x: Math.min(t, t + wide), y: near, w: wide === 0 ? 1 : 2, h: len }
            : { x: near, y: Math.min(t, t + wide), w: len, h: wide === 0 ? 1 : 2 },
          'closet',
        );
      }
    }
  }

  reconnectWarren(site, r, spine, horizontal);
}

/** Wire every surviving doorway on the warren's boundary back to its spine. */
function reconnectWarren(site: GenSite, r: Rect, spine: number, horizontal: boolean): void {
  const { grid } = site;
  const link = (ix: number, iy: number): void => {
    if (!grid.inBounds(ix, iy)) return;
    if (horizontal) carveLine(grid, ix, iy, ix, spine, Cell.Floor);
    else carveLine(grid, ix, iy, spine, iy, Cell.Floor);
  };
  for (let x = r.x; x < r.x + r.w; x++) {
    if (grid.isWalkable(x, r.y - 1)) link(x, r.y);
    if (grid.isWalkable(x, r.y + r.h)) link(x, r.y + r.h - 1);
  }
  for (let y = r.y; y < r.y + r.h; y++) {
    if (grid.isWalkable(r.x - 1, y)) link(r.x, y);
    if (grid.isWalkable(r.x + r.w, y)) link(r.x + r.w - 1, y);
  }
}

/* ------------------------------------------------------------------ *
 * Columns
 * ------------------------------------------------------------------ */

/**
 * The building's column grid: one lattice, one pitch, laid from the map origin
 * rather than from any region, so a column in one plate lines up with the
 * column two districts away. Marching columns disappearing into the fog is the
 * single most recognisable thing about this floor, and it only works if they
 * are genuinely regular.
 *
 * A column is skipped wherever it would not be holding up a ceiling so much as
 * blocking a gap: anything but open floor on all four sides, or another column
 * already within a cell of it.
 */
function columnGrid(site: GenSite, pitch: number): void {
  const { grid } = site;
  for (let y = 2; y < grid.h - 2; y += pitch) {
    for (let x = 2; x < grid.w - 2; x += pitch) {
      if (grid.at(x, y) !== Cell.Floor) continue;
      let open = true;
      for (let d = 0; d < 4 && open; d++) {
        if (!grid.isWalkable(x + DX[d]!, y + DY[d]!)) open = false;
      }
      if (!open || nearColumn(grid, x, y)) continue;
      grid.set(x, y, Cell.Pillar);
    }
  }
}

/** True when any of the eight neighbours already holds a column. */
function nearColumn(grid: Grid, x: number, y: number): boolean {
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      if (grid.at(x + dx, y + dy) === Cell.Pillar) return true;
    }
  }
  return false;
}
